import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useActivityTypes from '../../hooks/useActivityTypes';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Group as GroupIcon,
  Event as EventIcon,
  Mail as MailIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format, startOfWeek, endOfWeek, startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from '../../utils/axios';
import ParticipantsList from '../../components/admin/ParticipantsList';
import { fr } from 'date-fns/locale';
import { isFrenchPublicHoliday } from '../../utils/dates';
import { buildCalendarEvent } from '../../utils/activity';

// Setup the localizer for react-big-calendar
const locales = {
  'fr': fr
};

const localizer = dateFnsLocalizer({
  format: (date, formatStr) => format(date, formatStr, { locale: fr }),
  parse: (str) => new Date(str),
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay: (date) => date.getDay(),
  locales,
  messages: {
    previous: 'Précédent',
    next: 'Suivant',
    today: 'Aujourd\'hui',
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
  },
});

// Category colors for the calendar
const FALLBACK_CATEGORY_COLORS = {
  'Yoga Ashtanga': '#032D23', // Dark Green
  'Hatha Yoga': '#B8860B', // Dark Yellow
  'Yoga Vinyasa': '#404616', // Bright Green
  'Yoga Restauratif': '#452663',
  'Yin Yoga': '#CF5200',
  'Yoga des Énergies': '#4A601C',// Deep Purple
  'Shiatsu': '#890857', // Dark Red
  'Chi Nei Tsang': '#B75312', // Orange Red
  'Soin': '#792720', // Deep Blue
  'Ateliers': '#0F52BA', // Indigo
  'Pilates Doux': '#6D2348', // Indigo
  'Power Pilates': '#432C5F',
  'Pilates - Tous niveaux': '#6D2348', // Purple/ Deep Blue
  'Autres': '#00533E' // Gold
};

const FALLBACK_CATEGORY_DISPLAY_NAMES = {
  'Yoga Ashtanga': 'Yoga Ashtanga',
  'Hatha Yoga': 'Hatha Yoga',
  'Yoga Vinyasa': 'Yoga Vinyasa',
  'Yoga Restauratif': 'Yoga Restauratif',
  'Yin Yoga': 'Yin Yoga',
  'Yoga des Énergies': 'Yoga des Énergies',
  'Shiatsu': 'Shiatsu',
  'Chi Nei Tsang': 'CNT',
  'Soin': 'Soin',
  'Ateliers': 'Ateliers',
  'Pilates Doux': 'Pilates Doux',
  'Power Pilates': 'Power Pilates',
  'Pilates - Tous niveaux': 'Pilates - Tous niveaux',
  'Autres': 'Autres'
};

// Aliases kept for any residual module-level references
const categoryColors = FALLBACK_CATEGORY_COLORS;
const categoryDisplayNames = FALLBACK_CATEGORY_DISPLAY_NAMES;

// Custom styles for the calendar
const calendarStyles = {
  '.rbc-time-slot': {
    height: '35px',
    '@media (max-width: 768px)': {
      height: '30px'
    }
  },
  '.rbc-label': {
    fontSize: 'var(--time-slot-font-size, 0.85rem)',
    padding: '4px',
    minWidth: '150px',
    '@media (max-width: 768px)': {
      padding: '2px',
      minWidth: '40px',
      fontSize: '0.7rem'
    }
  },
  '.rbc-time-content': {
    fontSize: 'var(--time-slot-font-size, 0.85rem)',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem'
    }
  },
  '.rbc-time-header-cell': {
    fontSize: '0.85rem',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem'
    }
  },
  '.rbc-time-view': {
    fontSize: '0.85rem',
    height: '700px',
    width: '100%',
    '@media (max-width: 768px)': {
      fontSize: '0.7rem',
      height: '500px'
    }
  },
  '.rbc-timeslot-group': {
    minHeight: '35px',
    '@media (max-width: 768px)': {
      minHeight: '30px'
    }
  },
  '.rbc-time-column': {
    minWidth: '2000px',
    '@media (max-width: 768px)': {
      minWidth: 'auto'
    }
  },
  '.rbc-day-slot': {
    minWidth: '2000px',
    '@media (max-width: 768px)': {
      minWidth: 'auto'
    }
  },
  '.rbc-event': {
    backgroundColor: 'transparent',
    border: 'none'
  },
  '.rbc-event-content': {
    whiteSpace: 'pre-wrap',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    '@media (max-width: 768px)': {
      lineHeight: 1.2,
      fontSize: '0.75rem'
    },
    lineHeight: 1.3,
    fontSize: '0.8rem',
    maxHeight: '5.2em',
    overflow: 'hidden'
  },
  '.rbc-events-container': {
    marginRight: '0 !important',
    height: '100%',
    width: '100%',
    position: 'relative'
  },
  '.rbc-day-slot .rbc-events-container': {
    marginRight: '0 !important',
    position: 'relative',
    width: '100%'
  },
  '.rbc-day-slot .rbc-event': {
    position: 'absolute !important',
    width: '100% !important',
    left: '0 !important',
    right: '0 !important',
    height: '100% !important',
    margin: '0 !important',
    padding: '12px',
    '@media (max-width: 768px)': {
      padding: '8px'
    }
  },
  '.rbc-event-overlaps': {
    width: '50% !important',
    zIndex: 2
  },
  '.rbc-event-continues-after': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0
  },
  '.rbc-day-slot .rbc-events-container .rbc-event.rbc-selected': {
    background: 'none'
  },
  '.rbc-event.rbc-selected': {
    backgroundColor: 'inherit',
    outline: 'none'
  },
  '.rbc-event:focus': {
    outline: 'none'
  },
  '.rbc-day-slot .rbc-events-container .rbc-event.rbc-event-overlaps': {
    width: 'calc(50% - 10px)',
    '&:first-of-type': {
      left: '5px'
    },
    '&:last-of-type': {
      left: 'calc(50% + 5px)'
    }
  },
  '.rbc-day-slot .rbc-background-event': {
    opacity: 0.75
  },
  '.rbc-day-slot .rbc-event-continues-before': {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0
  },
  '.rbc-day-slot .rbc-event-continues-earlier': {
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0
  },
  '.rbc-day-slot .rbc-event-continues-later': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0
  },
  '.rbc-toolbar': {
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '8px',
      '& .rbc-btn-group': {
        margin: '4px',
        display: 'flex',
        flexWrap: 'nowrap',
        width: 'auto',
        '& button': {
          flex: '1',
          padding: '8px',
          minWidth: '60px',
          fontSize: '0.9rem',
          whiteSpace: 'nowrap',
          backgroundColor: '#F3E6D8',
          '&.rbc-active': {
            backgroundColor: '#942B0D',
            color: 'white'
          }
        }
      }
    }
  },
  '.rbc-toolbar-label': {
    '@media (max-width: 768px)': {
      order: -1,
      width: '100%',
      textAlign: 'center',
      fontSize: '1.1rem',
      padding: '8px 0',
      fontWeight: 500
    }
  },
  '.rbc-btn-group': {
    '@media (max-width: 768px)': {
      margin: '0',
      '& button': {
        padding: '4px 8px',
        fontSize: '0.8rem'
      }
    }
  }
};

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={0}>
    <CardContent>
      <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 2,
            bgcolor: `${color}.lighter`,
            color: `${color}.dark`,
            display: 'flex',
          }}
        >
          {React.cloneElement(icon, {
            sx: { fontSize: { xs: '1.25rem', sm: '1.5rem' } }
          })}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              fontWeight: 600,
              lineHeight: 1.3,
              mb: 0.5
            }}
          >
            {value}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              mb: subtitle ? 0.5 : 0
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              display="block"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { names, colors, displayNames, byName: typesByName, ready } = useActivityTypes();
  const categoryColors = ready ? { ...FALLBACK_CATEGORY_COLORS, ...colors } : FALLBACK_CATEGORY_COLORS;
  const categoryDisplayNames = ready ? { ...FALLBACK_CATEGORY_DISPLAY_NAMES, ...displayNames } : FALLBACK_CATEGORY_DISPLAY_NAMES;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Add resize handler for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalActivities: 0,
    totalNewsletters: 0,
    activeSubscriptions: 0,
    recentActivities: [],
    recentUsers: [],
  });
  const [calendarView, setCalendarView] = useState(isMobile ? 'day' : 'week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [visibleRange, setVisibleRange] = useState(() => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 1 });
    const end = endOfWeek(today, { weekStartsOn: 1 });
    return { start, end };
  });
  const [visibleActivities, setVisibleActivities] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    day: ''
  });

  // Add state for participants dialog
  const [participantsDialog, setParticipantsDialog] = useState({
    open: false,
    activity: null,
    participants: []
  });

  // Categories from API when available
  const categories = ready && names.length ? names : Object.keys(categoryColors);

  // Add authentication check
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const fetchDashboardData = useCallback(async () => {
    if (!isAuthenticated || !isAdmin) return;
    
    try {
      setLoading(true);
      const [
        usersRes,
        activitiesRes,
        newslettersRes,
      ] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/activities', {
          params: {
            sort: 'date',
            limit: 5000
          }
        }),
        axios.get('/api/newsletters'),
      ]);

      // Sort activities by date in ascending order without filtering past activities
      const sortedActivities = activitiesRes.data
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      setStats(prev => ({
        ...prev,
        totalUsers: usersRes.data.length,
        totalActivities: activitiesRes.data.length,
        totalNewsletters: newslettersRes.data.length,
        activeSubscriptions: usersRes.data.filter(user => user.maxActivities > 0).length,
        recentActivities: sortedActivities,
        recentUsers: usersRes.data
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5),
      }));
      setError(null);
    } catch (err) {
      setError(t('errorFetchingDashboard'));
    } finally {
      setLoading(false);
    }
  }, [t, isAuthenticated, isAdmin]);

  // Get visible activities based on current view and filters
  const getVisibleActivities = React.useCallback(() => {
    return stats.recentActivities.filter(activity => {
      const activityDate = new Date(activity.date);
      
      // Only check if the activity is within the visible range
      const isInVisibleRange = activityDate >= visibleRange.start && activityDate <= visibleRange.end;
      
      // Apply category filter if set
      const matchesCategory = !filters.category || activity.category === filters.category;
      const matchesDay = !filters.day || format(activityDate, 'yyyy-MM-dd') === filters.day;
      
      return isInVisibleRange && matchesCategory && matchesDay;
    });
  }, [stats.recentActivities, visibleRange, filters]);

  // Update visible activities when relevant dependencies change
  useEffect(() => {
    const filtered = getVisibleActivities();
    setVisibleActivities(filtered);
  }, [getVisibleActivities]);

  // Update initial data fetch with 120-day window
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const currentDate = new Date();
        
        // Calculate 120-day window (30 days before and 90 days after)
        const windowStart = new Date(currentDate);
        windowStart.setDate(windowStart.getDate() - 30);
        const windowEnd = new Date(currentDate);
        windowEnd.setDate(windowEnd.getDate() + 90);
        
        const params = {
          startDate: windowStart.toISOString(),
          endDate: windowEnd.toISOString()
        };

        if (filters.category) {
          params.category = filters.category;
        }

        const response = await axios.get('/api/activities', { params });
        
        if (Array.isArray(response.data)) {
          // Update activities in stats
          setStats(prev => ({
            ...prev,
            recentActivities: response.data
          }));
          
          // Update visible activities immediately
          const filtered = response.data.filter(activity => {
            const activityDate = new Date(activity.date);
            const isInVisibleRange = activityDate >= visibleRange.start && activityDate <= visibleRange.end;
            const matchesCategory = !filters.category || activity.category === filters.category;
            return isInVisibleRange && matchesCategory;
          });
          
          setVisibleActivities(filtered);
          setError(null);
        }
      } catch (err) {
        setError(t('errorFetchingActivities'));
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []); // Run only on mount

  // Initial data fetch
  useEffect(() => {
    if (!authLoading && isAuthenticated && isAdmin) {
      fetchDashboardData();
    }
  }, [authLoading, isAuthenticated, isAdmin, fetchDashboardData]);

  const handleCloseParticipantsDialog = () => {
    setParticipantsDialog({
      open: false,
      activity: null,
      participants: []
    });
  };

  const handleParticipantUpdate = async () => {
    if (participantsDialog.activity) {
      await fetchParticipants(participantsDialog.activity);
      await fetchDashboardData(); // Refresh dashboard data
    }
  };

  const activityColumns = [
    {
      field: 'title',
      headerName: t('activity'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'category',
      headerName: t('category'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'day',
      headerName: t('day'),
      width: 100,
      valueGetter: (params) => {
        const date = new Date(params.row.date);
        return format(date, 'dd/MM', { locale: fr });
      },
    },
    {
      field: 'hour',
      headerName: t('time'),
      width: 80,
      valueGetter: (params) => {
        const date = new Date(params.row.date);
        return format(date, 'HH:mm', { locale: fr });
      },
    },
    {
      field: 'participants',
      headerName: t('participants'),
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography>
            {params.row.currentParticipants}/{params.row.maxParticipants}
          </Typography>
          <Tooltip title={t('viewParticipants')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                fetchParticipants(params.row);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Add function to fetch participants
  const fetchParticipants = async (activity) => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/login', { replace: true });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/activities/${activity._id}/participants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setParticipantsDialog({
        open: true,
        activity: activity,
        participants: response.data
      });
    } catch (error) {
      setError(t('errorFetchingParticipants'));
      
      if (error.response?.status === 401) {
        navigate('/login', { replace: true });
      }
    }
  };

  const userColumns = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.name || t('notAvailable')
    },
    {
      field: 'email',
      headerName: t('email'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'subscriptionStatus',
      headerName: t('status'),
      width: 120,
      valueGetter: (params) => params.row.subscriptionStatus || 'inactive',
      renderCell: (params) => (
        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 1,
            bgcolor: 
              params.value === 'active' ? 'success.lighter' :
              params.value === 'expired' ? 'error.lighter' : 'grey.100',
            color: 
              params.value === 'active' ? 'success.dark' :
              params.value === 'expired' ? 'error.dark' : 'text.secondary',
          }}
        >
          {t(params.value)}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: t('joined'),
      width: 180,
      valueFormatter: (params) =>
        format(new Date(params.value), 'PPp'),
    },
  ];

  // Style public holidays
  const dayPropGetter = (date) => {
    if (isFrenchPublicHoliday(date)) {
      return {
        style: {
          backgroundColor: '#2E7D32',
          color: 'white',
        },
      };
    }
    return {};
  };

  // Handle event selection
  const handleSelectEvent = (event) => {
    if (event.activity) {
      fetchParticipants(event.activity);
    }
  };

  // Update handleNavigate to use 120-day window
  const handleNavigate = async (newDate) => {
    setCurrentDate(newDate);
    
    let start, end;
    
    switch (calendarView) {
      case 'month':
        start = startOfMonth(newDate);
        end = endOfMonth(newDate);
        break;
      case 'week':
        start = startOfWeek(newDate, { weekStartsOn: 1 });
        end = endOfWeek(newDate, { weekStartsOn: 1 });
        break;
      default: // day view
        start = startOfDay(newDate);
        end = endOfDay(newDate);
    }
    
    setVisibleRange({ start, end });
    
    // Use 120-day window for fetching (30 days before and 90 days after)
    const windowStart = new Date(newDate);
    windowStart.setDate(windowStart.getDate() - 30);
    const windowEnd = new Date(newDate);
    windowEnd.setDate(windowEnd.getDate() + 90);
    
    try {
      setLoading(true);
      const params = {
        startDate: windowStart.toISOString(),
        endDate: windowEnd.toISOString()
      };

      if (filters.category) {
        params.category = filters.category;
      }

      const response = await axios.get('/api/activities', { params });
      
      if (Array.isArray(response.data)) {
        // Update activities in stats
        setStats(prev => ({
          ...prev,
          recentActivities: response.data
        }));
        
        // Update visible activities immediately
        const filtered = response.data.filter(activity => {
          const activityDate = new Date(activity.date);
          const isInVisibleRange = activityDate >= start && activityDate <= end;
          const matchesCategory = !filters.category || activity.category === filters.category;
          return isInVisibleRange && matchesCategory;
        });
        
        setVisibleActivities(filtered);
        setError(null);
      }
    } catch (err) {
      setError(t('errorFetchingActivities'));
    } finally {
      setLoading(false);
    }
  };

  // Update handleViewChange to use 120-day window
  const handleViewChange = async (newView) => {
    setCalendarView(newView);
    
    let start, end;
    
    switch (newView) {
      case 'month':
        start = startOfMonth(currentDate);
        end = endOfMonth(currentDate);
        break;
      case 'week':
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
        break;
      default: // day view
        start = startOfDay(currentDate);
        end = endOfDay(currentDate);
    }
    
    setVisibleRange({ start, end });
    
    // Use 120-day window for fetching (30 days before and 90 days after)
    const windowStart = new Date(currentDate);
    windowStart.setDate(windowStart.getDate() - 30);
    const windowEnd = new Date(currentDate);
    windowEnd.setDate(windowEnd.getDate() + 90);
    
    try {
      setLoading(true);
      const params = {
        startDate: windowStart.toISOString(),
        endDate: windowEnd.toISOString()
      };

      if (filters.category) {
        params.category = filters.category;
      }

      const response = await axios.get('/api/activities', { params });
      
      if (Array.isArray(response.data)) {
        // Update activities in stats
        setStats(prev => ({
          ...prev,
          recentActivities: response.data
        }));
        
        // Update visible activities immediately
        const filtered = response.data.filter(activity => {
          const activityDate = new Date(activity.date);
          const isInVisibleRange = activityDate >= start && activityDate <= end;
          const matchesCategory = !filters.category || activity.category === filters.category;
          return isInVisibleRange && matchesCategory;
        });
        
        setVisibleActivities(filtered);
        setError(null);
      }
    } catch (err) {
      setError(t('errorFetchingActivities'));
    } finally {
      setLoading(false);
    }
  };

  // Add filter change handler
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add clear filter handler
  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  if (authLoading || loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Stats Section */}
        <Grid item xs={12}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('totalUsers')}
                value={stats.totalUsers}
                icon={<GroupIcon />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('totalActivities')}
                value={stats.totalActivities}
                icon={<EventIcon />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('activeSubscriptions')}
                value={stats.activeSubscriptions}
                icon={<TrendingUpIcon />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title={t('newsletters')}
                value={stats.totalNewsletters}
                icon={<MailIcon />}
                color="warning"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Calendar Section */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              {t('activityCalendar')}
            </Typography>
            <Calendar
              localizer={localizer}
              defaultView={isMobile ? 'day' : 'week'}
              view={calendarView}
              date={currentDate}
              events={stats.recentActivities.map((activity) =>
                buildCalendarEvent(activity, { displayNames: categoryDisplayNames, typesByName, t })
              )}
              startAccessor="start"
              endAccessor="end"
              step={30}
              timeslots={2}
              dayLayoutAlgorithm="no-overlap"
              views={['month', 'week', 'day']}
              style={{ 
                height: isMobile ? '400px' : '700px',
                width: '100%',
                '--time-slot-font-size': isMobile ? '0.7rem' : '0.85rem',
                ...calendarStyles
              }}
              eventPropGetter={(event) => ({
                style: {
                  backgroundColor: categoryColors[event.category] || '#808080',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '3px',
                  whiteSpace: 'pre-line',
                  padding: '4px',
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                  maxHeight: '5.2em',
                  overflow: 'hidden'
                }
              })}
              dayPropGetter={dayPropGetter}
              onSelectEvent={handleSelectEvent}
                                                                min={new Date(new Date().setHours(7, 0, 0))}
                   max={new Date(new Date().setHours(21, 0, 0))}
                   scrollToTime={new Date(new Date().setHours(7, 0, 0))}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              messages={{
                month: t('monthView'),
                week: t('weekView'),
                day: t('dayView'),
                today: t('today'),
                previous: t('previous'),
                next: t('next')
              }}
              formats={{
                eventTimeRangeFormat: () => '',
                timeGutterFormat: (date, culture, localizer) =>
                  localizer.format(date, 'HH:mm', culture),
                dayFormat: (date, culture, localizer) =>
                  `${localizer.format(date, 'EEE', culture)} ${localizer.format(date, 'd', culture)}`,
                dayHeaderFormat: (date, culture, localizer) =>
                  `${localizer.format(date, 'EEE', culture)} ${localizer.format(date, 'd', culture)}`
              }}
              culture="fr"
            />
          </Paper>
        </Grid>

        {/* Filter Section */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 3 }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              {t('filters')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('category')}</InputLabel>
                  <Select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    label={t('category')}
                    endAdornment={
                      filters.category && (
                        <IconButton
                          size="small"
                          sx={{ 
                            position: 'absolute',
                            right: 32,
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }}
                          onClick={() => handleClearFilter('category')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="">{t('all')}</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('day')}</InputLabel>
                  <Select
                    name="day"
                    value={filters.day}
                    onChange={handleFilterChange}
                    label={t('day')}
                    endAdornment={
                      filters.day && (
                        <IconButton
                          size="small"
                          sx={{ 
                            position: 'absolute',
                            right: 32,
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }}
                          onClick={() => handleClearFilter('day')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="">{t('all')}</MenuItem>
                    {visibleActivities.map((activity) => (
                      <MenuItem key={activity.date} value={format(new Date(activity.date), 'yyyy-MM-dd')}>
                        {format(new Date(activity.date), 'dd/MM', { locale: fr })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Activities Section */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 },
              mb: { xs: 2, sm: 3 }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: { xs: 2, sm: 3 }
              }}
            >
              <Typography 
                variant="h6"
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  fontWeight: 600
                }}
              >
                {t('activities')} - {calendarView === 'month' 
                  ? t('monthView')
                  : calendarView === 'week'
                  ? t('weekView')
                  : t('dayView')}
              </Typography>
            </Box>
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={visibleActivities}
                columns={activityColumns}
                pageSize={25}
                rowsPerPageOptions={[10, 25, 50, 100]}
                getRowId={(row) => row._id}
                disableSelectionOnClick
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'date', sort: 'asc' }],
                  },
                  pagination: {
                    pageSize: 25,
                  },
                }}
                sx={{
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Recent Users Section */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: { xs: 2, sm: 3 }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600
              }}
            >
              {t('recentUsers')}
            </Typography>
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={stats.recentUsers}
                columns={userColumns}
                pageSize={5}
                rowsPerPageOptions={[5]}
                getRowId={(row) => row._id}
                sx={{
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Participants Dialog */}
      <Dialog
        open={participantsDialog.open}
        onClose={handleCloseParticipantsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('participantsList')}
            </Typography>
            <IconButton onClick={handleCloseParticipantsDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <ParticipantsList
            activity={participantsDialog.activity}
            participants={participantsDialog.participants}
            onParticipantUpdate={handleParticipantUpdate}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard; 